import React from "react";
import { Link } from "gatsby";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Col, Dropdown, Row } from "react-bootstrap";
import Moment from "moment";

import ArticleCard from "./ArticleCard";
import ShareButtons from "./ShareButtons";
import Pagination from "./Pagination";
import { selectBlogTopics } from "../redux/slices/articleSlice";

import "../styles/components/BlogPostsList.scss";

const TopicFilterDropdownToggle = React.forwardRef(
    ({ children, onClick }, ref) => (
        <button
            ref={ref}
            className="topic-filter-toggle d-flex align-items-center"
            onClick={e => {
                e.preventDefault();
                onClick(e);
            }}
        >
            <span className="btn btn-primary topic-filter-toggle-text">
                {children}
            </span>
            <span className="topic-filter-toggle-icon" />
        </button>
    )
);

const TopicFilter = ({ current, blogPrefix, type }) => {
    const blogTopics = useSelector(state => selectBlogTopics(state, type));

    return (
        <div className="topic-filter">
            <Dropdown>
                <Dropdown.Toggle as={TopicFilterDropdownToggle}>
                    {current?.name || "All Topics"}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                    <Dropdown.Item
                        as={Link}
                        to={`${blogPrefix}/`}
                        active={!current}
                    >
                        All Topics
                    </Dropdown.Item>
                    {blogTopics?.map(topic => (
                        <Dropdown.Item
                            as={Link}
                            to={`${blogPrefix}/topic/${topic.slug}/`}
                            key={topic.slug}
                            active={topic.slug === current?.slug}
                        >
                            {topic.name}
                        </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
};

// blogPosts need to be props as they are obtained from the Page query (because they need
// to be filtered by topic, which is a dynamic value).
const BlogPostsList = ({
    topic,
    blogPosts,
    currentPage,
    numPages,
    blogPrefix,
    type,
}) => {
    const baseUrl = topic ? `${blogPrefix}/topic/${topic.slug}` : blogPrefix;

    return (
        <div className="blog-posts-list ms-auto">
            <Row className="mb-4">
                <Col>
                    <ShareButtons />
                </Col>
                <Col className="d-flex justify-content-end align-items-center">
                    <TopicFilter
                        current={topic}
                        blogPrefix={blogPrefix}
                        type={type}
                    />
                </Col>
            </Row>
            <Row>
                {blogPosts?.map(blogPost => (
                    <Col sm={4} className="mb-4" key={blogPost.slug}>
                        <ArticleCard post={blogPost} urlPrefix={blogPrefix} />
                    </Col>
                ))}
            </Row>
            <Row>
                <Col className="d-flex justify-content-center">
                    <Pagination
                        currentPage={currentPage}
                        baseUrl={baseUrl}
                        totalPages={numPages}
                        siblingCount={2}
                    />
                </Col>
            </Row>
        </div>
    );
};

BlogPostsList.propTypes = {
    topic: PropTypes.shape({
        name: PropTypes.string.isRequired,
        slug: PropTypes.string.isRequired,
    }),
    blogPosts: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            slug: PropTypes.string,
            publishDate: PropTypes.instanceOf(Moment),
            image: PropTypes.shape({
                title: PropTypes.string,
                description: PropTypes.string,
                gatsbyImageData: PropTypes.object,
                file: PropTypes.shape({ url: PropTypes.string }),
            }),
            topics: PropTypes.arrayOf(
                PropTypes.shape({
                    slug: PropTypes.string.isRequired,
                    name: PropTypes.string.isRequired,
                })
            ),
            body: PropTypes.shape({ raw: PropTypes.string.isRequired }),
        })
    ).isRequired,
};

export default BlogPostsList;
